import apiClient from './apiClient';

// =====================================================
// OPERATION 0: BUNDLE RECEIPT ENTRY (NEW)
// =====================================================

// Get shipped consignments ready for bundle receipt in Greige section
export const getShippedConsignments = async () => {
  const response = await apiClient.get('/sections/greige/shipped-consignments');
  return response.data;
};

// Start bundle receipt process
export const startBundleReceipt = async (entryId) => {
  const response = await apiClient.post(`/sections/greige/start-bundle-receipt/${entryId}`);
  return response.data;
};

// Get bundle entry status
export const getBundleEntry = async (entryId) => {
  const response = await apiClient.get(`/sections/greige/bundle-entry/${entryId}`);
  return response.data;
};

// Scan a bundle barcode
export const scanBundle = async (entryId, bundleBarcode) => {
  const response = await apiClient.post(
    `/sections/greige/scan-bundle/${entryId}`,
    null,
    { params: { bundle_barcode: bundleBarcode } }
  );
  return response.data;
};

// Complete bundle receipt
export const completeBundleReceipt = async (entryId) => {
  const response = await apiClient.post(`/sections/greige/complete-bundle-receipt/${entryId}`);
  return response.data;
};

// Remove a scanned bundle
export const removeBundle = async (entryId, bundleBarcode) => {
  const response = await apiClient.delete(`/sections/greige/remove-bundle/${entryId}/${bundleBarcode}`);
  return response.data;
};

// =====================================================
// EXISTING GREIGE APIs
// =====================================================

// Get vendors
export const getVendors = async () => {
  const response = await apiClient.get('/sections/greige/vendors');
  return response.data;
};

// Get sort nos
export const getSortNos = async (vendorName = null) => {
  const params = vendorName ? { vendor_name: vendorName } : {};
  const response = await apiClient.get('/sections/greige/sort-nos', { params });
  return response.data;
};

// Get colors
export const getColors = async () => {
  const response = await apiClient.get('/sections/greige/colors');
  return response.data;
};

// Get damage types
export const getDamageTypes = async () => {
  const response = await apiClient.get('/sections/greige/damage-types');
  return response.data;
};

// Get staff stats
export const getStaffStats = async (staffId, operationName = null) => {
  const params = operationName ? { operation_name: operationName } : {};
  const response = await apiClient.get(`/sections/greige/staff-stats/${staffId}`, { params });
  return response.data;
};

// Submit sort assignment
export const submitSortAssignment = async (staffId, vendorName, sortNo, colorCode, bundleBarcodes) => {
  const params = {
    staff_id: staffId,
    vendor_name: vendorName,
    sort_no: sortNo,
    color_code: colorCode,
    bundle_barcodes: bundleBarcodes
  };
  const response = await apiClient.post('/sections/greige/sort-assignment/submit', null, { params });
  return response.data;
};

// Submit bundle split
export const submitBundleSplit = async (staffId, originalBundle, newBundles) => {
  const params = {
    staff_id: staffId,
    original_bundle: originalBundle,
    new_bundles: newBundles
  };
  const response = await apiClient.post('/sections/greige/bundle-split/submit', null, { params });
  return response.data;
};

// Validate bundle for inspection
export const validateInspectionBundle = async (bundleBarcode) => {
  const response = await apiClient.post('/sections/greige/inspection/validate-bundle', null, {
    params: { bundle_barcode: bundleBarcode }
  });
  return response.data;
};

// Submit inspection
export const submitInspection = async (staffId, bundleBarcode, {
  productType = 'dhoti', runningMtrQty = null, reed, pick, width, length, kuriGap, bundleDamages = [],
} = {}) => {
  const response = await apiClient.post('/sections/greige/inspection/submit', {
    staff_id: staffId,
    bundle_barcode: bundleBarcode,
    product_type: productType,
    running_mtr_qty: runningMtrQty,
    reed, pick, width, length,
    kuri_gap: kuriGap,
    bundle_damages: bundleDamages,
  });
  return response.data;
};

// Submit design conversion
export const submitDesignConversion = async (staffId, originalSortNo, newSortNo, bundleBarcodes) => {
  const params = {
    staff_id: staffId,
    original_sort_no: originalSortNo,
    new_sort_no: newSortNo,
    bundle_barcodes: bundleBarcodes
  };
  const response = await apiClient.post('/sections/greige/design-conversion/submit', null, { params });
  return response.data;
};

// Submit pinning
export const submitPinning = async (staffId, bundleBarcodes) => {
  const response = await apiClient.post('/sections/greige/pinning/submit', {
    staff_id: staffId,
    bundle_barcodes: bundleBarcodes,
  });
  return response.data;
};

// Submit marker writing (per-bundle qty required in the new flow)
export const submitMarkerWriting = async (staffId, bundleBarcodes, bundleQuantities = []) => {
  const response = await apiClient.post('/sections/greige/marker-writing/submit', {
    staff_id: staffId,
    bundle_barcodes: bundleBarcodes,
    bundle_quantities: bundleQuantities,
  });
  return response.data;
};

// Submit packing
export const submitPacking = async (staffId, bundleBarcodes) => {
  const response = await apiClient.post('/sections/greige/packing/submit', {
    staff_id: staffId,
    bundle_barcodes: bundleBarcodes,
  });
  return response.data;
};


// =====================================================
// GREIGE SECTION REPORTS
// =====================================================

export const getGreigeOperations = async () => {
  const response = await apiClient.get('/sections/greige/reports/operations');
  return response.data;
};

export const getGreigeEnhancedSummary = async (params = {}) => {
  const response = await apiClient.get('/sections/greige/reports/enhanced-summary', { params });
  return response.data;
};

export const getGreigeTransactions = async (params = {}) => {
  const response = await apiClient.get('/sections/greige/reports/transactions', { params });
  return response.data;
};

export const getGreigeClosingStock = async (params = {}) => {
  const response = await apiClient.get('/sections/greige/reports/closing-stock', { params });
  return response.data;
};

